






































import {
  defineComponent,
  computed,
  onMounted,
  reactive,
} from '@vue/composition-api';
import { useStore } from '@/hooks/useStore';
import { UserActionTypes } from '@/store/modules/user';
import { waitForUserAndMasters } from '@/lib/masterHelper';
import { isShutokouDirectDriver } from '@/lib/userAffiliation';
import {
  waitForJohaisetsuMasters,
  isJohaisetsuTypeOfGroup,
  getJohaisetsuHanName,
  filterJohaisetsuTypesByGroup,
} from '@/lib/johaisetsu/johaisetsuHelper';
import { getJohaisetsuDeviceId } from '@/lib/johaisetsu/johaisetsuDeviceId';
import { provideBaseArrivalNotification } from '@/composables/useBaseArrivalNotification';
import { provideLogoutCleanup } from '@/composables/useLogoutCleanup';
import { provideSagyouPhase, SagyouPhaseState } from '@/composables/useSagyouPhase';
import { provideMyJohaisetsuCar, MyJohaisetsuCarContainer } from '@/composables/useMyJohaisetsuCar';
import { provideJohaisetsuSagyouContext } from '@/components/Sp/Johaisetsu/composables/useJohaisetsuSagyouContext';
import johaisetsuCarApi from '@/apis/johaisetsu/johaisetsu_car';
import { JohaisetsuCarShowResponse } from '@/models/apis/johaisetsu/johaisetsuCarResponse';
import {
  JohaisetsuSagyouContext,
  JohaisetsuSagyou,
  JohaisetsuSagyouMenuKey,
  JohaisetsuSagyouCurrentReport,
  SagyouInProgress,
  JohaisetsuSagyouPortalState,
} from './types';
import { JOHAISETSU_SAGYOU_MENU, JOHAISETSU_SAGYOU_MENU_MAP } from './consts';

import SettouSagyouCommon from '@/components/Sp/Johaisetsu/SettouSagyou/SettouSagyouCommon/index.vue';
import SettouPatrol from '@/components/Sp/Johaisetsu/SettouPatrol/SettouPatrol/index.vue';
import EnsuiPlantStockReport from '@/components/Sp/Johaisetsu/EnsuiPlantStockReport/index.vue';
import SettouOnsiteManagement from '@/components/Sp/Johaisetsu/SettouPatrol/SettouOnsiteManagement/index.vue';
import NotificationArea from '@/components/lib/NotificationArea/index.vue';
import SpHeader from '@/components/lib/SpHeader.vue';

const componentMap = {
  SettouSagyouCommon,
  SettouPatrol,
  EnsuiPlantStockReport,
  SettouOnsiteManagement,
};

const DEFAULT_HEADER_TITLE = '雪凍作業';

function isJohaisetsuSagyouMenuKey(value: string): value is JohaisetsuSagyouMenuKey {
  return JOHAISETSU_SAGYOU_MENU_MAP.has(value);
}

// car.johaisetsu_type(小分類key)から作業種別を逆引きする
function johaisetsuTypeToMenuKey(johaisetsuType: string): JohaisetsuSagyouMenuKey | null {
  const sagyouMenu = JOHAISETSU_SAGYOU_MENU.find(m => {
    return m.johaisetsuTypeGroup && isJohaisetsuTypeOfGroup(johaisetsuType, m.johaisetsuTypeGroup);
  });
  return sagyouMenu?.key ?? null;
}

// 作業進行中判定はサーバー側のgetCurrentに委ねる(無ければnullが返る)
async function getCurrentReportByMenuKey(
  menuKey: JohaisetsuSagyouMenuKey,
  carId: number,
): Promise<JohaisetsuSagyouCurrentReport | null> {
  const menu = JOHAISETSU_SAGYOU_MENU_MAP.get(menuKey);
  if (!menu?.getCurrentReport) return null; // 塩水プラント在庫報告など報告書の概念がない種別

  const { data: report } = await menu.getCurrentReport({ johaisetsu_car_id: carId });
  return report;
}

async function fetchMyJohaisetsuCar(userId: number): Promise<JohaisetsuCarShowResponse | null> {
  const deviceId = getJohaisetsuDeviceId(userId);
  if (!deviceId) return null; // 新端末(または別ユーザーの端末) = carなし
  return johaisetsuCarApi.getMyJohaisetsuCar(deviceId);
}

// 進行中の作業があればそのメニューkeyを返す。無ければ null
async function getSagyouInProgress(car: JohaisetsuCarShowResponse | null): Promise<SagyouInProgress | null> {
  if (!car?.id) return null;

  const menuKey = johaisetsuTypeToMenuKey(car.johaisetsu_type);
  if (!menuKey) return null;

  const report = await getCurrentReportByMenuKey(menuKey, car.id);
  // getMyJohaisetsuCar/getCurrent は該当なし時に空オブジェクトを返すため、
  // 存在チェックは report 自体の truthy 判定ではなく report?.id で行う
  if (!report?.id) return null;

  return { menuKey, report };
}

export default defineComponent({
  name: 'sp-johaisetsu-sagyou',
  components: {
    SettouSagyouCommon,
    SettouPatrol,
    EnsuiPlantStockReport,
    SettouOnsiteManagement,
    NotificationArea,
    SpHeader,
  },
  setup() {
    const store = useStore();
    const userState = store.state.user;
    const state = reactive<JohaisetsuSagyouPortalState>({
      isReady: false,
      selectedMenuKey: '',
      inProgressSagyou: null,
      isShowingCarFetchErrorModal: false,
    });

    // 親画面に基地到着通知で作業種別を活性に
    const baseArrivalNotification = provideBaseArrivalNotification();
    baseArrivalNotification.on('baseArrival', () => {
      state.inProgressSagyou = null;
    });

    const runLogoutCleanup = provideLogoutCleanup();
    // 作業ステータス、作業開始前であれば、作業種別を活性化
    const sagyouPhase = reactive<SagyouPhaseState>({ isPreWork: true });
    provideSagyouPhase(sagyouPhase);

    const johaisetsuCar = reactive<MyJohaisetsuCarContainer>({ current: null, carFetchFailed: false });
    provideMyJohaisetsuCar(johaisetsuCar);

    const johaisetsuHanName = computed<string>(() => {
      return getJohaisetsuHanName(userState.johaisetsu_han);
    });
    const availableMenu = computed<JohaisetsuSagyou[]>(() => {
      return JOHAISETSU_SAGYOU_MENU.filter(m => !!userState.abilityMap[m.ability]);
    });
    const selectedMenu = computed<JohaisetsuSagyou | null>(() => {
      return availableMenu.value.find(m => m.key === state.selectedMenuKey) ?? null;
    });
    const headerTitle = computed<string>(() => {
      return selectedMenu.value?.label ?? DEFAULT_HEADER_TITLE;
    });

    const currentMenuReport = computed<JohaisetsuSagyouCurrentReport | null>(() => {
      return state.inProgressSagyou?.report ?? null;
    });

    const resolveJohaisetsuTypeGroupName = computed<string>(() => {
      if (!selectedMenu.value) return '';
      if (!selectedMenu.value.johaisetsuTypeGroup) return selectedMenu.value.label;

      const johaisetsuTypes = filterJohaisetsuTypesByGroup(selectedMenu.value.johaisetsuTypeGroup);
      return johaisetsuTypes[0]?.johaisetsu_type ?? selectedMenu.value?.label;
    });

    const johaisetsuSagyouContext = (): JohaisetsuSagyouContext => ({
      ability: selectedMenu.value?.ability ?? 0,
      johaisetsuTypeGroup: selectedMenu.value?.johaisetsuTypeGroup ?? '',
      johaisetsuTypeGroupName: resolveJohaisetsuTypeGroupName.value,
      sagyouActionLabel: selectedMenu.value?.sagyouActionLabel,
      inProgressReport: currentMenuReport.value,
    });
    provideJohaisetsuSagyouContext(johaisetsuSagyouContext);

    const onSelectMenu = (event: Event): void => {
      const selectEl = event.target as HTMLSelectElement;
      if (!isJohaisetsuSagyouMenuKey(selectEl.value)) return;

      state.selectedMenuKey = selectEl.value;
    };

    const logout = async(): Promise<void> => {
      await runLogoutCleanup();
      await store.dispatch(UserActionTypes.LOGOUT);
      location.href = '/login';
    };

    const loadJohaisetsuCarState = async(): Promise<void> => {
      let inProgress: SagyouInProgress | null = null;
      try {
        const car = await fetchMyJohaisetsuCar(userState.id);
        johaisetsuCar.current = car;
        inProgress = await getSagyouInProgress(car);
        johaisetsuCar.carFetchFailed = false;
      } catch (e) {
        state.isShowingCarFetchErrorModal = true;
        johaisetsuCar.carFetchFailed = true;
      }
      if (!inProgress) return;

      const isInProgressMenuAvailable = availableMenu.value.some(m => m.key === inProgress?.menuKey);
      state.selectedMenuKey = isInProgressMenuAvailable ? inProgress.menuKey : (availableMenu.value[0]?.key ?? '');
      state.inProgressSagyou = isInProgressMenuAvailable ? inProgress : null;
    };

    onMounted(async() => {
      await Promise.all([waitForUserAndMasters(), waitForJohaisetsuMasters()]);
      // johaisetsu_role が driver のユーザーは班に紐付かないため、班未所属でも利用可とする
      if (!userState.johaisetsu_han?.length && !isShutokouDirectDriver(userState.johaisetsu_role)) {
        location.href = '/login';
        return;
      }

      const envElement: HTMLMetaElement | null = document.querySelector("meta[name='viewport']");
      if (envElement) {
        envElement.setAttribute(
          'content',
          'width=device-width,initial-scale=1.0,minimum-scale=1.0,maximum-scale=1.0,user-scalable=no',
        );
      }
      await loadJohaisetsuCarState();
      state.isReady = true;
    });

    return {
      state,
      sagyouPhase,
      johaisetsuHanName,
      headerTitle,
      availableMenu,
      selectedMenu,
      componentMap,
      onSelectMenu,
      logout,
    };
  },
});
